import { useIntl } from '@capsicum/common';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { isWCPledgeBasketEmptyAtom, wcPledgeBasketLengthAtom } from '../data-access';
import { type WCPledgeBasketFooterProps, WCPledgeBasketFooterVariantEnum } from '../models';

import { useWCPledgeBasketState } from './useWCPledgeBasketState';

export const useWCPledgeBasketFooter = ({ variant }: WCPledgeBasketFooterProps) => {
	const { $t } = useIntl();
	const {
		formattedTotalPledgeAmount,
		formattedTotalPledgeAmountUSDWithDoubleTilde,
		insufficientBalance,
		isAnyAmountInputBelowDefaultPledgeAmount,
		maxTeamsPerPledge
	} = useWCPledgeBasketState();
	const isBasketEmpty = useAtomValue(isWCPledgeBasketEmptyAtom);
	const basketLength = useAtomValue(wcPledgeBasketLengthAtom);
	const basketLengthWithLabel =
		basketLength === 1
			? $t('worldChampionshipPledge.basketFooter.teamSingular', { count: basketLength })
			: $t('worldChampionshipPledge.basketFooter.teamPlural', { count: basketLength });

	const hasReachedMaximumTeams = basketLength >= maxTeamsPerPledge;
	const maximumTeamsReachedText = $t('worldChampionshipPledge.basketFooter.maximumTeamsReached');

	const insufficientText = $t('worldChampionshipPledge.basketFooter.insufficientText');
	const topupLink = $t('worldChampionshipPledge.basketFooter.topupLink');

	const variantLookUpTable: Record<WCPledgeBasketFooterVariantEnum, { buttonText: string; hasPrice: boolean }> = {
		[WCPledgeBasketFooterVariantEnum.BASKET_DETAILS]: {
			buttonText: $t('worldChampionshipPledge.basketFooter.basketDetailsButtonText'),
			hasPrice: true
		},
		[WCPledgeBasketFooterVariantEnum.ITEM_SELECTION]: {
			buttonText: $t('global.button.continue'),
			hasPrice: false
		}
	};

	const isButtonDisabled = useMemo(() => {
		if (variant === WCPledgeBasketFooterVariantEnum.ITEM_SELECTION) {
			return isBasketEmpty || basketLength > maxTeamsPerPledge;
		}

		return (
			isBasketEmpty ||
			(insufficientBalance && variantLookUpTable[variant].hasPrice) ||
			isAnyAmountInputBelowDefaultPledgeAmount
		);
	}, [
		isBasketEmpty,
		hasReachedMaximumTeams,
		insufficientBalance,
		variantLookUpTable[variant].hasPrice,
		isAnyAmountInputBelowDefaultPledgeAmount
	]);

	return {
		basketLengthWithLabel,
		buttonText: variantLookUpTable[variant].buttonText,
		formattedTotalPledgeAmount,
		formattedTotalPledgeAmountUSDWithDoubleTilde,
		hasPrice: variantLookUpTable[variant].hasPrice,
		hasReachedMaximumTeams,
		insufficientBalance,
		insufficientText,
		isBasketEmpty,
		isButtonDisabled,
		maximumTeamsReachedText,
		topupLink
	};
};
